/**
 * Global Excel Export Utility
 * @file src/utils/ExportToExcelUtility.js
 */

import * as XLSX from 'xlsx';
import moment from 'moment';

// Global date format
export const globalDateFormat = "DD-MM-YYYY hh:mm a";

/**
 * Generate file name for export
 */
export const generateFileName = (prefix, dateRange = null) => {
    const parts = [prefix];

    if (dateRange?.startDate && dateRange?.endDate) {
        const startStr = moment(dateRange.startDate).format('DD-MM-YYYY');
        const endStr = moment(dateRange.endDate).format('DD-MM-YYYY');

        if (startStr === endStr) {
            parts.push(startStr);
        } else {
            parts.push(`${startStr}_to_${endStr}`);
        }
    }

    parts.push(moment().format('DDMMYYYY_HHmmss'));

    return `${parts.join('_')}.xlsx`;
};

/**
 * Format date value
 */
export const formatDate = (val) => {
    if (!val) return '-';
    return moment(val).format(globalDateFormat);
};

/**
 * Export data to Excel
 * 
 * @param {Object} config
 * @param {string} config.fileNamePrefix - Prefix for file name
 * @param {Object} config.dateRange - { startDate, endDate }
 * @param {string} config.fileName - Custom file name (optional)
 * @param {Array} config.sheets - Array of sheet configs
 * @param {string} config.sheets[].name - Sheet name
 * @param {Array} config.sheets[].data - Data array
 * @param {Array} config.sheets[].columns - Column configs [{ header, key, transform }]
 * 
 * @example
 * exportToExcel({
 *     fileNamePrefix: 'report',
 *     dateRange: { startDate, endDate },
 *     sheets: [{
 *         name: 'Data',
 *         data: myData,
 *         columns: [
 *             { header: 'S.No', key: '_index' },
 *             { header: 'Name', key: 'name' },
 *             { header: 'Date', key: 'created_at', transform: (val) => formatDate(val) }
 *         ]
 *     }]
 * });
 */
export const exportToExcel = ({ fileName = null, fileNamePrefix = 'export', dateRange = null, sheets = [] }) => {
    try {
        // Validate
        if (!sheets || sheets.length === 0) {
            console.warn('No sheets provided');
            return { success: false, error: 'No sheets provided' };
        }

        const hasData = sheets.some(sheet => sheet.data?.length > 0);
        if (!hasData) {
            console.warn('No data to export');
            return { success: false, error: 'No data to export' };
        }

        // Create workbook
        const workbook = XLSX.utils.book_new();

        sheets.forEach((sheet, sheetIndex) => {
            const { name = `Sheet${sheetIndex + 1}`, data = [], columns = null } = sheet;

            if (!data || data.length === 0) return;

            // Transform data if columns provided
            let sheetData = data;

            if (columns && columns.length > 0) {
                sheetData = data.map((item, rowIndex) => {
                    const row = {};

                    columns.forEach(col => {
                        let value;

                        // Handle serial number
                        if (col.key === '_index') {
                            value = rowIndex + 1;
                        } else {
                            value = item[col.key];
                        }

                        // Apply transform if provided
                        if (col.transform && typeof col.transform === 'function') {
                            value = col.transform(value, item, rowIndex);
                        }

                        row[col.header] = value ?? '-';
                    });

                    return row;
                });
            }

            // Create worksheet
            const worksheet = XLSX.utils.json_to_sheet(sheetData);

            // Auto column width
            if (sheetData.length > 0) {
                const headers = Object.keys(sheetData[0]);
                worksheet['!cols'] = headers.map(header => {
                    let maxWidth = header.length;
                    sheetData.forEach(row => {
                        const len = row[header] != null ? String(row[header]).length : 0;
                        if (len > maxWidth) maxWidth = len;
                    });
                    return { wch: Math.min(maxWidth + 2, 50) };
                });
            }

            // Sanitize sheet name (max 31 chars)
            let sheetName = (name || 'Sheet').substring(0, 31).replace(/[\\/*?:\[\]]/g, '').trim() || 'Sheet';

            // Ensure unique name
            let uniqueName = sheetName;
            let counter = 1;
            while (workbook.SheetNames.includes(uniqueName)) {
                uniqueName = `${sheetName.substring(0, 28)}_${counter}`;
                counter++;
            }

            XLSX.utils.book_append_sheet(workbook, worksheet, uniqueName);
        });

        // Generate file name
        const finalFileName = fileName || generateFileName(fileNamePrefix, dateRange);

        // Download
        XLSX.writeFile(workbook, finalFileName);

        return { success: true, fileName: finalFileName };

    } catch (error) {
        console.error('Export failed', error);
        return { success: false, error: error.message };
    }
};

export default exportToExcel;